import { useParams, Link } from "react-router-dom";
import SEO from "@/components/feature/SEO";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/base/VideoCard";
import { videos } from "@/mocks/videos";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === id);

  if (!video) {
    return (
      <div className="pt-24 pb-12">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-900 mb-2">视频未找到</h2>
          <p className="text-foreground-500 mb-4">该视频可能已下架或不存在</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-home-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.id !== video.id && (v.categorySlug === video.categorySlug || v.platform === video.platform))
    .slice(0, 6);

  const articleTitle = `${video.title} - 久久视频高清在线观看`;
  const articleDesc = `${video.title}，${video.description.substring(0, 120)}。久久视频聚合香蕉视频、红桃视频等平台资源，高清不缓冲。`;

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: video.category, href: `/category/${video.categorySlug}` },
    { label: video.title },
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: video.title,
    description: video.description,
    image: video.coverImage,
    datePublished: video.releaseDate,
    author: {
      "@type": "Organization",
      name: "久久视频",
    },
    publisher: {
      "@type": "Organization",
      name: "久久视频",
    },
  };

  return (
    <>
      <SEO
        title={articleTitle}
        description={articleDesc}
        keywords={`${video.title},${video.category},${video.platform},在线观看,高清,久久视频`}
        ogImage={video.coverImage}
        ogType="article"
        schemaData={articleSchema}
      />

      <main className="pt-20 pb-12">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <Breadcrumb items={breadcrumbItems} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <article className="lg:col-span-2">
            {/* Video Cover */}
            <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-6">
              <img
                src={video.coverImage}
                alt={video.title}
                title={`${video.title} - 久久视频高清在线观看`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 cursor-pointer hover:bg-primary-500 transition-colors">
                  <i className="ri-play-fill text-3xl md:text-4xl"></i>
                </div>
              </div>
              <span className="absolute bottom-4 right-4 bg-black/70 text-white text-sm px-3 py-1 rounded whitespace-nowrap">
                {video.duration}
              </span>
            </div>

            {/* Title & Meta */}
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              {video.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                <strong className="text-foreground-900">{video.rating}</strong> 分
              </span>
              <span>{video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万次播放` : `${video.views}次播放`}</span>
              <span>{video.releaseDate}</span>
              <span className="bg-secondary-100 text-secondary-900 px-2 py-0.5 rounded-full text-xs">
                {video.category}
              </span>
              <span className="text-xs text-foreground-400">来源：{video.platform}</span>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600 border border-background-200"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Description */}
            <div className="prose prose-sm max-w-none mb-8">
              <h2 className="text-lg font-bold text-foreground-900 font-heading mb-3">内容介绍</h2>
              <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                {video.description}
              </p>
            </div>

            {/* Content */}
            <div className="bg-background-100 rounded-lg p-5 mb-8">
              <h2 className="text-lg font-bold text-foreground-900 font-heading mb-3">详情</h2>
              <div className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                {video.content}
              </div>
            </div>

            {/* Watch Button */}
            <div className="flex flex-col sm:flex-row items-center gap-3 p-5 bg-accent-100/50 rounded-lg">
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground-900">在 {video.platform} 观看完整版</p>
                <p className="text-xs text-foreground-500 mt-1">点击下方按钮跳转至{video.platform}观看高清完整版</p>
              </div>
              <a
                href="#"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                rel="nofollow"
              >
                <i className="ri-play-circle-line"></i> 立即观看
              </a>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            {/* Video Info Card */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 mb-6">
              <h3 className="text-base font-bold text-foreground-900 font-heading mb-4">影片信息</h3>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-foreground-500">分类</dt>
                  <dd className="text-sm font-medium text-foreground-800">{video.category}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-foreground-500">来源平台</dt>
                  <dd className="text-sm font-medium text-foreground-800">{video.platform}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-foreground-500">时长</dt>
                  <dd className="text-sm font-medium text-foreground-800">{video.duration}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-foreground-500">上映日期</dt>
                  <dd className="text-sm font-medium text-foreground-800">{video.releaseDate}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-foreground-500">评分</dt>
                  <dd className="text-sm font-medium text-accent-600 flex items-center gap-1">
                    <i className="ri-star-fill"></i> {video.rating}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-foreground-500">播放量</dt>
                  <dd className="text-sm font-medium text-foreground-800">
                    {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Related Videos */}
            <div>
              <h3 className="text-base font-bold text-foreground-900 font-heading mb-4">相关推荐</h3>
              <div className="space-y-3">
                {relatedVideos.map((rv) => (
                  <Link
                    key={rv.id}
                    to={`/article/${rv.id}`}
                    className="flex gap-3 group cursor-pointer"
                  >
                    <div className="w-28 h-16 shrink-0 rounded overflow-hidden">
                      <img
                        src={rv.coverImage}
                        alt={rv.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {rv.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 text-xs text-foreground-400">
                        <span>{rv.category}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                          {rv.rating}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
    </>
  );
}